import React from 'react';
import { Incident } from '../../Interfaces';
import { useNavigate } from 'react-router-dom';

interface LocationSummaryCardProps {
  location: {
    id: number;
    name: string;
    latitude: number;
    longitude: number;
    incidents: Incident[];
    walkReportStats: {
      last72Hours: number;
      lastWeek: number;
    };
  };
}

const LocationSummaryCard: React.FC<LocationSummaryCardProps> = ({ location }) => {
  const navigate = useNavigate();
  // Count incidents by type
  const allIncidents = location.incidents;
  const criticalIncidents = allIncidents.filter(incident => incident.typeId === 1);
  const importantIncidents = allIncidents.filter(incident => incident.typeId === 2);
  const regularIncidents = allIncidents.filter(incident => incident.typeId === 3);

  const handleIncidentClick = (incident: Incident) => {
    navigate(`/app/incidents/${incident.id}`, {
      state: {
        from: `/app/locations/${location.id}#incidents`
      }
    });
  };

  const getPriorityColor = (typeId: number) => {
    switch (typeId) {
      case 1:
        return 'border-red-500 bg-red-50';
      case 2:
        return 'border-yellow-500 bg-yellow-50';
      case 3:
        return 'border-green-500 bg-green-50';
      default:
        return 'border-gray-500 bg-gray-50';
    }
  };

  const getPriorityText = (typeId: number) => {
    switch (typeId) {
      case 1:
        return 'Critical';
      case 2:
        return 'Important';
      case 3:
        return 'Regular';
      default:
        return 'Unknown';
    }
  };

  const getPriorityTextColor = (typeId: number) => {
    switch (typeId) {
      case 1:
        return 'text-red-700';
      case 2:
        return 'text-yellow-700';
      case 3:
        return 'text-green-700';
      default:
        return 'text-gray-700';
    }
  };

  const getStatusTag = (incident: Incident) => {
    if (incident.resolvedAt) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-medium">
          Resolved
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800 font-medium">
          Unresolved
        </span>
      );
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 mb-2">
      <div className="flex justify-between items-start mb-2">
        <h2 className="text-lg font-bold">{location.name}</h2>
        <div className="flex gap-2">
          <div className="bg-blue-50 px-2 py-1 rounded text-sm">
            <span className="text-gray-600">72h: </span>
            <span className={`font-bold ${location.walkReportStats.last72Hours < 2 ? 'text-red-600' : 'text-blue-600'
              }`}>
              {location.walkReportStats.last72Hours}
            </span>
          </div>
          <div className="bg-green-50 px-2 py-1 rounded text-sm">
            <span className="text-gray-600">Week: </span>
            <span className={`font-bold ${location.walkReportStats.lastWeek < 5 ? 'text-red-600' :
              location.walkReportStats.lastWeek < 10 ? 'text-orange-600' :
                'text-green-600'
              }`}>
              {location.walkReportStats.lastWeek}
            </span>
          </div>
          <div className="bg-red-50 px-2 py-1 rounded text-sm">
            <span className="text-gray-600">Critical: </span>
            <span className={`font-bold ${criticalIncidents.length === 0 ? 'text-gray-900' : 'text-red-600'}`}>
              {criticalIncidents.length}
            </span>
          </div>
          <div className="bg-yellow-50 px-2 py-1 rounded text-sm">
            <span className="text-gray-600">Important: </span>
            <span className={`font-bold ${importantIncidents.length === 0 ? 'text-gray-900' : 'text-yellow-600'}`}>
              {importantIncidents.length}
            </span>
          </div>
          <div className="bg-green-50 px-2 py-1 rounded text-sm">
            <span className="text-gray-600">Regular: </span>
            <span className={`font-bold ${regularIncidents.length === 0 ? 'text-gray-900' : 'text-green-600'}`}>
              {regularIncidents.length}
            </span>
          </div>
        </div>
      </div>

      {allIncidents.length > 0 && (
        <div className="border-t pt-4">
          <h3 className="text-md font-semibold mb-3">All Incidents</h3>

          {/* Desktop and Tablet Grid View */}
          <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-5 gap-4">
            {allIncidents
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map(incident => (
                <div
                  key={incident.id}
                  className={`border-l-4 rounded-lg p-3 cursor-pointer hover:shadow-md transition-shadow ${getPriorityColor(incident.typeId)}`}
                  onClick={() => handleIncidentClick(incident)}
                >
                  <div className="mb-2">
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-semibold ${getPriorityTextColor(incident.typeId)}`}>
                        {getPriorityText(incident.typeId)}
                      </span>
                      <span className="text-xs text-gray-500">
                        {new Date(incident.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="text-sm text-gray-700 line-clamp-2">
                      {incident.notes[0]?.note || 'No description available'}
                    </div>
                    <div className="mt-2">
                      {getStatusTag(incident)}
                    </div>
                  </div>

                  {/* Image Section */}
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-2">
                    {incident.images && incident.images.length > 0 ? (
                      <img
                        src={incident.images[0].presignedUrl}
                        alt={`Incident ${incident.id}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <div className={`w-full h-full flex items-center justify-center text-gray-500 text-sm ${incident.images && incident.images.length > 0 ? 'hidden' : ''}`}>
                      No Image
                    </div>
                  </div>

                  {/* Incident Types */}
                  {incident.incidentTexts && incident.incidentTexts.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {incident.incidentTexts.slice(0, 2).map(text => (
                        <span
                          key={text.id}
                          className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800"
                        >
                          {text.incidentNoteText.text}
                        </span>
                      ))}
                      {incident.incidentTexts.length > 2 && (
                        <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                          +{incident.incidentTexts.length - 2}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
          </div>

          {/* Mobile List View */}
          <div className="md:hidden space-y-2">
            {allIncidents
              .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
              .map(incident => (
                <div
                  key={incident.id}
                  className={`border-l-4 pl-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${getPriorityColor(incident.typeId)}`}
                  onClick={() => handleIncidentClick(incident)}
                >
                  <div className="flex justify-between items-start mb-1">
                    <span className={`text-sm font-semibold ${getPriorityTextColor(incident.typeId)}`}>
                      {getPriorityText(incident.typeId)}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(incident.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="text-sm text-gray-700 mb-2">
                    {incident.notes[0]?.note || 'No description available'}
                  </div>
                  <div className="mb-2">
                    {getStatusTag(incident)}
                  </div>

                  {/* Incident Types */}
                  {incident.incidentTexts && incident.incidentTexts.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {incident.incidentTexts.map(text => (
                        <span
                          key={text.id}
                          className="px-1.5 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800"
                        >
                          {text.incidentNoteText.text}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSummaryCard; 